import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MapPin, Bell } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

const LocationsWaitlist = () => {
  const [email, setEmail] = useState("");
  const [zip, setZip] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setIsSubmitting(true);
    try {
      // Save to newsletter_subscribers table with source
      const { error } = await supabase.from("newsletter_subscribers").insert({
        email,
        source: `location_waitlist_${zip || "no_zip"}`,
      });

      if (error) {
        if (error.code === "23505") {
          toast({
            title: "Already on the list!",
            description: "You're already signed up for notifications.",
          });
        } else {
          throw error;
        }
      } else {
        toast({
          title: "You're on the list!",
          description: "We'll notify you when a provider opens near you.",
        });
        setEmail("");
        setZip("");
      }
    } catch (error) {
      console.error("Waitlist error:", error);
      toast({
        title: "Something went wrong",
        description: "Please try again later.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-16 bg-[#0D1B2A] border-t border-white/10">
      <div className="container mx-auto px-4">
        <div className="max-w-2xl mx-auto text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#ffdb4d]/20 mb-6">
            <MapPin className="h-8 w-8 text-[#ffdb4d]" />
          </div>
          
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
            Don't See a Provider Near You?
          </h2>
          <p className="text-white/60 mb-8">
            Join our waitlist and be the first to know when a TrueWellness provider opens in your area.
          </p>

          <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
            <Input
              type="email"
              placeholder="Your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="flex-1 bg-white/10 border-white/20 text-white placeholder:text-white/50"
            />
            <Input
              type="text"
              placeholder="ZIP code (optional)"
              value={zip}
              onChange={(e) => setZip(e.target.value)}
              className="w-full sm:w-32 bg-white/10 border-white/20 text-white placeholder:text-white/50"
              maxLength={5}
            />
            <Button
              type="submit"
              disabled={isSubmitting}
              className="bg-[#ffdb4d] hover:bg-[#ffdb4d]/90 text-[#0D1B2A] font-semibold"
            >
              <Bell className="h-4 w-4 mr-2" />
              Notify Me
            </Button>
          </form>
        </div>
      </div>
    </section>
  );
};

export default LocationsWaitlist;
